import { ActionButton, type ActionSize } from './ui/ActionButton';
import { CompactAthletePicker } from './CompactAthletePicker';
import { normalizeAthleteSearch } from '../utils/athleteSearch';
import { AthleteFolders } from './AthleteFolders';
import { useAuth } from '../hooks/useAuth';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { AthleteItem } from '../services/api';
import { AthleteSettingsButton } from './AthleteSettingsButton';
import { AthleteRemoveButton } from './AthleteRemoveButton';

interface AthleteSwitcherProps {
  size?: ActionSize;
  /** Optional custom content for the existing picker trigger. */
  triggerContent?: React.ReactNode;
  /** All athletes under this coach (from /me response). */
  athletes: AthleteItem[];
  /** Currently selected athlete ID — null means none selected yet. */
  selectedId: number | null;
  /** Called when the user picks an athlete. */
  onChange: (id: number) => void;
  /** Opens athlete settings for the given ID. */
  onOpenSettings?: (id: number) => void;
  /** Removes athlete from coach list. */
  onRemoveAthlete?: (id: number, name: string) => void;
  removingAthleteId?: number | null;
  /** Compact pill for mobile header — narrower trigger + smaller avatar. */
  compact?: boolean;
}

const AVATAR_SIZE = 22;

function PersonIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  );
}

/** Circular avatar — profile photo when available, otherwise a person glyph. */
function AthleteAvatar({
  src,
  size = AVATAR_SIZE,
  className,
}: {
  src?: string | null;
  size?: number;
  className?: string;
}) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          border: '1.5px solid var(--surface-sunken)',
        }}
      />
    );
  }

  return (
    <span
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-sunken)',
        color: 'var(--fg-4)',
      }}
    >
      <PersonIcon size={Math.round(size * 0.55)} />
    </span>
  );
}

function ChevDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

/**
 * Global athlete dropdown rendered in the title bar.
 * 
 * Shown only when user.role === 'coach' and there are athletes.
 * Each row has the athlete name and a small settings icon.
 */
export function AthleteSwitcher({
  athletes,
  selectedId,
  onChange,
  onOpenSettings,
  onRemoveAthlete,
  removingAthleteId = null,
  triggerContent,
  compact = false,
  size = 'sm',
}: AthleteSwitcherProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(58);
  const [search, setSearch] = useState('');
  useEffect(() => { if (!open) setSearch(''); }, [open]);
  const matchesSearch = (athlete: AthleteItem) => normalizeAthleteSearch(athlete.full_name).includes(normalizeAthleteSearch(search));
  const searchField = <label className="athlete-picker-search">
    <input type="search" value={search} onChange={event => setSearch(event.target.value)}
      aria-label={t('athletePicker.search')} placeholder={t('athletePicker.search')} />
  </label>;

  const selectedAthlete = athletes.find(a => a.id === selectedId);
  const label = selectedAthlete ? selectedAthlete.full_name : t('athletePicker.title');
  const avatarSize = compact ? 18 : AVATAR_SIZE;

  const athleteRows = (
    <>
      {athletes.length === 0 && (
        <div className="athlete-switcher-modal__empty">
          {t('app.noAthletesLinked')}
        </div>
      )}
      {searchField}
      <AthleteFolders ownerId={user?.id} athletes={athletes} showHeading={false} accordion searchQuery={search}>{visible => <>
      {visible.filter(matchesSearch).map(a => (
        <AthleteRow
          key={a.id}
          label={a.full_name}
          profileImage={a.profile_image}
          isShared={a.coaching_relationship === 'assistant'}
          active={selectedId === a.id}
          removing={removingAthleteId === a.id}
          onClick={() => { onChange(a.id); setOpen(false); }}
          onSettingsClick={onOpenSettings ? (e) => {
            e.stopPropagation();
            setOpen(false);
            onOpenSettings(a.id);
          } : null}
          onRemoveClick={
            onRemoveAthlete && a.coaching_relationship !== 'assistant'
              ? (e) => {
                  e.stopPropagation();
                  onRemoveAthlete(a.id, a.full_name);
                }
              : null
          }
        />
      ))}
      {search.trim() && !visible.some(matchesSearch) && <p className="athlete-picker-empty">{t('athletePicker.noResults')}</p>}
      </>}</AthleteFolders>
    </>
  );

  return (
    <div
      className={`title-bar-no-drag athlete-switcher${compact ? ' athlete-switcher--compact' : ''}`}
      style={{ position: 'relative' }}
    >
      <ActionButton size={size} aria-expanded={open} aria-haspopup="dialog"
        className={`athlete-switcher-trigger${compact ? ' athlete-switcher-trigger--compact' : ''}`}
        style={compact ? compactTriggerStyle : undefined}
        onClick={event => { setPanelTop(event.currentTarget.getBoundingClientRect().bottom + 6); setOpen(o => !o); }}>
        {triggerContent ?? <>
        <AthleteAvatar
          src={selectedAthlete?.profile_image}
          size={avatarSize}
          className="athlete-switcher__avatar"
        />
        <span
          className="athlete-switcher__label"
          style={{ ...triggerLabelStyle, ...(compact ? compactLabelStyle : null) }}
        >
          {label}
        </span>
        <ChevDownIcon />
        </>}
      </ActionButton>

      {open && compact && <CompactAthletePicker athletes={athletes} selectedId={selectedId} top={panelTop}
        onClose={() => setOpen(false)} onPick={id => {onChange(id); setOpen(false);}}
        onOpenSettings={onOpenSettings ? id => {setOpen(false); onOpenSettings(id);} : undefined}/>}

      {open && !compact && (
        <div className="title-bar-no-drag athlete-switcher-menu" style={menuStyle}>
          {athleteRows}
        </div>
      )}
    </div>
  );
}

function AthleteRow({
  label, profileImage, isShared, active, removing, onClick, onSettingsClick, onRemoveClick,
}: {
  label: string;
  profileImage?: string | null;
  isShared?: boolean;
  active: boolean;
  removing: boolean;
  onClick: () => void;
  onSettingsClick: ((e: React.MouseEvent) => void) | null;
  onRemoveClick: ((e: React.MouseEvent) => void) | null;
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={rowWrapStyle(active, hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={onClick}
        style={rowStyle(active, hovered)}
      >
        <AthleteAvatar src={profileImage} size={20} />
        {active && <span style={{ fontSize: 10, marginInlineEnd: -2 }}>✓</span>}
        <span style={rowLabelStyle}>
          <span style={rowNameStyle}>{label}</span>
          {isShared && (
            <span style={rowSharedBadgeStyle}>
              {t('app.shared')}
            </span>
          )}
        </span>
      </button>
      {onSettingsClick && (
        <AthleteSettingsButton
          onClick={onSettingsClick}
          title={t('app.athleteSettings')}
        />
      )}
      {onRemoveClick && (
        <AthleteRemoveButton
          loading={removing}
          onClick={onRemoveClick}
          title={t('app.removeAthlete.title')}
        />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

/** Matches NmButton sm layout; sizing comes from nm-btn classes. */
const triggerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  whiteSpace: 'nowrap',
  width: '100%',
};

const triggerLabelStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 12,
};

const compactTriggerStyle: React.CSSProperties = {
  width: 'auto',
  maxWidth: '100%',
  gap: 4,
};

const compactLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
};

const menuStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 2500,
  background: 'var(--surface-0)', borderRadius: 'var(--r-3)',
  boxShadow: 'var(--nm-raised)', border: '1px solid var(--surface-sunken)',
  padding: '4px 0', minWidth: 210, maxHeight: 320, overflowY: 'auto',
};

const rowWrapStyle = (active: boolean, hovered: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  paddingInlineEnd: 6,
  background: active ? 'rgba(223,32,99,0.08)' : hovered ? 'var(--surface-sunken)' : 'none',
  transition: 'background 0.08s',
});

const rowStyle = (active: boolean, hovered: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 6,
  flex: 1, minWidth: 0,
  paddingBlock: 7,
  paddingInline: '12px 6px',
  background: 'none',
  border: 'none', cursor: 'pointer', textAlign: 'start' as const,
  fontSize: 12, fontWeight: active ? 700 : 500,
  color: active ? 'var(--color-accent)' : 'var(--fg-1)',
  fontFamily: 'var(--font-body)',
});

const rowLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
};

const rowNameStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowSharedBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: 9,
  color: '#a78bfa',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};
